import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from './api';
import { fmtWh } from './time';
import { titleEn } from './lineName';

const R = `${API}/returns`;

/**
 * Returns → Unclaimed COD — the working to-do.
 *
 * Shaped like the Manual refunds tab: a place the information lives, with a
 * to-do built in. The job on each row is "delete this order in the shop", which
 * is what actually puts the stock back — so Done is a record that a human did
 * it, not an instruction to the WMS.
 *
 * The analysis table (country trends, repeat customers, CSV) lives separately
 * under Statistics → Unclaimed COD. This screen is for getting work done.
 */

type Progress = { state: 'unpolished' | 'polished' | 'replenished';
                  expected: number; scanned: number; shelved: number };
type Case = {
  id: number; case_code: string; order_number: string; store_name: string;
  customer_name: string | null; country_code: string | null;
  order_value: number | null; currency: string | null; value_eur: number | null;
  received_at: string | null; order_date: string | null;
  status: string; source: string; progress: Progress;
  done_by: string | null; deleted_in_shop_at: string | null;
  registered_via?: string | null; received_by?: string | null; notes?: string | null; carrier_state?: string | null;
  lines: { id: number; product_name: string; article_number: string;
           qty_ordered: number; qty_verified: number }[];
};

const STATE_TONE: Record<Progress['state'], string> = {
  unpolished:  'bg-gray-700 text-gray-200',
  polished:    'bg-blue-800 text-blue-100',
  replenished: 'bg-green-800 text-green-100',
};
const STATE_LABEL: Record<Progress['state'], string> = {
  unpolished: 'Unpolished', polished: 'Polished', replenished: 'Replenished',
};

const money = (v: number | null, c?: string | null) =>
  v === null || v === undefined ? '—' : `${c === 'EUR' || !c ? '€' : ''}${Number(v).toFixed(2)}${c && c !== 'EUR' ? ' ' + c : ''}`;
// warehouse clock, not the browser's (time.ts)
const day = (d: string | null) => (d ? fmtWh(d, 'date') : '—');

export default function UnclaimedCod() {
  const [cases, setCases] = useState<Case[]>([]);
  const [archived, setArchived] = useState(false);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // Register a case directly (2026-09-29): the tab's entry point — the same core the CS
  // assistant uses through MCP (register_unclaimed_cod). No R-order, no shop write.
  const [regOpen, setRegOpen] = useState(false);
  const [regOrder, setRegOrder] = useState('');
  const [regPreview, setRegPreview] = useState<any | null>(null);
  const [regQty, setRegQty] = useState<Record<string, number>>({});
  const [regShort, setRegShort] = useState(false);
  const [regDate, setRegDate] = useState('');
  const [regNote, setRegNote] = useState('');
  const [regBusy, setRegBusy] = useState(false);
  const [regMsg, setRegMsg] = useState('');
  const lookup = async () => {
    setRegMsg(''); setRegPreview(null);
    const r = await authFetch(`${R}/unclaimed-cod/preview?order=${encodeURIComponent(regOrder.trim())}`);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setRegMsg(d.detail || `HTTP ${r.status}`); return; }
    setRegPreview(d);
    const q: Record<string, number> = {};
    (d.lines || []).forEach((l: any) => { q[String(l.variation_id)] = l.ordered_qty; });
    setRegQty(q); setRegShort(false);
  };
  const register = async () => {
    if (!regPreview) return;
    setRegBusy(true); setRegMsg('');
    const items = regShort ? (regPreview.lines || []).map((l: any) => ({ variation_id: l.variation_id, qty: regQty[String(l.variation_id)] ?? 0 })) : undefined;
    const r = await authFetch(`${R}/unclaimed-cod/cases`, { method: 'POST', body: JSON.stringify({
      order: regPreview.order_id, items, received_on: regDate || undefined, note: regNote || undefined }) });
    const d = await r.json().catch(() => ({}));
    setRegBusy(false);
    if (!r.ok) { setRegMsg(d.detail || `HTTP ${r.status}`); return; }
    setRegMsg(`✓ ${d.case_code} registered for ${d.order_number} — ${d.pieces_back} of ${d.pieces_ordered} pieces${d.short ? ' (SHORT — in Review)' : ''}.`);
    setRegPreview(null); setRegOrder(''); setRegNote(''); setRegDate(''); load();
  };

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ archived: String(archived) });
    if (q.trim()) qs.set('q', q.trim());
    authFetch(`${R}/unclaimed-cod/todo?${qs}`)
      .then(r => r.json()).then(d => { setCases(d.cases || []); setErr(''); })
      .catch(() => setErr('Could not load the list'))
      .finally(() => setLoading(false));
  }, [archived, q]);
  // debounced so typing in the search box does not fire a request per keystroke
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);

  const toggle = (id: number) =>
    setOpen(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const markDone = async (c: Case, force = false, note = '', deletedOn?: string | null) => {
    if (deletedOn === undefined) {
      // the sheets' "deletion date": when the order was deleted in the shop (default today)
      const d = window.prompt(`Order ${c.order_number} deleted in the shop on (YYYY-MM-DD; empty = today):`, '');
      if (d === null) return;
      deletedOn = d.trim() || null;
    }
    const res = await authFetch(`${R}/unclaimed-cod/cases/${c.id}/done`, {
      method: 'POST', body: JSON.stringify({ ...(force ? { force, note } : {}), ...(deletedOn ? { deleted_on: deletedOn } : {}) }),
    });
    if (res.ok) { load(); return; }
    const j = await res.json().catch(() => ({}));
    // the backend refuses while pieces are still in the grey box — offer the override
    // rather than just reporting the refusal, since a short parcel is a real situation.
    // The override is a manager's call and needs a note saying why (recorded on the
    // case); the backend enforces both, this just asks up front.
    if (res.status === 400 && !force) {
      const why = window.prompt(`${j.detail}\n\nRecord it as done anyway? Say why (a manager or admin must do this):`);
      if (why === null) return;
      if (!why.trim()) { setErr('A note saying why is required to record it short.'); return; }
      markDone(c, true, why.trim(), deletedOn ?? null);
    } else { setErr(j.detail || 'Could not mark it done'); }
  };

  const total = cases.reduce((s, c) => s + (c.value_eur || 0), 0);

  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-4 mb-1">
        <h2 className="text-lg font-semibold text-white">Unclaimed COD</h2>
        <span className="text-sm text-gray-400">
          {cases.length} {archived ? 'done' : 'to deal with'} · €{total.toFixed(2)}
        </span>
        <div className="flex-1" />
        <button onClick={() => { setRegOpen(o => !o); setRegMsg(''); }}
          className="px-3 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-500 text-white">
          {regOpen ? 'Close' : '+ Register a case'}
        </button>
        <button onClick={() => setArchived(a => !a)}
          className="px-3 py-1.5 rounded text-sm bg-gray-800 text-gray-300 hover:text-white">
          {archived ? '← Back to the list' : 'Show archived'}
        </button>
      </div>
      {regOpen && (
        <div className="mb-4 rounded-lg border border-gray-800 bg-[#1a1d27] p-4 space-y-3">
          <div className="text-xs text-gray-500">A cash-on-delivery parcel came back never collected: register it here (or the CS assistant does it through MCP). The WMS fills customer, value and EUR from the order. Deleting the order in the shop stays your act — mark it Done afterwards with the date.</div>
          <div className="flex flex-wrap gap-2 items-center">
            <input value={regOrder} onChange={e => setRegOrder(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') lookup(); }}
              placeholder="Order number, e.g. CZ5440" className="keep-width w-48 bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm" />
            <button onClick={lookup} disabled={!regOrder.trim()} className="px-3 py-1.5 rounded text-sm bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-40">Look up</button>
            <input type="date" value={regDate} onChange={e => setRegDate(e.target.value)} title="Received on (empty = today)"
              className="keep-width bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm" />
            <input value={regNote} onChange={e => setRegNote(e.target.value)} placeholder="note (optional)"
              className="flex-1 min-w-[12rem] bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm" />
          </div>
          {regPreview && (
            <div className="text-sm space-y-2">
              <div className="text-gray-300">{regPreview.store} · <span className="text-white font-medium">{regPreview.order_number}</span> · {regPreview.customer_name || '—'} · {regPreview.country_code || ''} · {regPreview.carrier || ''}
                {' · '}{regPreview.value != null ? `${Number(regPreview.value).toFixed(2)} ${regPreview.currency || ''}` : '—'}{regPreview.value_eur != null && regPreview.currency !== 'EUR' ? ` (€${Number(regPreview.value_eur).toFixed(2)})` : ''}
                {' · '}<span className={regPreview.payment_kind === 'cod' ? 'text-emerald-300' : 'text-red-300'}>{regPreview.payment_method || '?'} ({regPreview.payment_kind})</span></div>
              {regPreview.existing_case && <div className="text-amber-300 text-xs">Already registered as {regPreview.existing_case.case_code} ({regPreview.existing_case.status}).</div>}
              {regPreview.payment_kind !== 'cod' && <div className="text-red-300 text-xs">Not a cash-on-delivery order — an unclaimed prepaid parcel is a return, not an Unclaimed COD case.</div>}
              <label className="flex items-center gap-2 text-xs text-gray-400"><input type="checkbox" checked={regShort} onChange={e => setRegShort(e.target.checked)} /> Not everything came back (say how many per line)</label>
              {regShort && (regPreview.lines || []).map((l: any) => (
                <div key={l.variation_id} className="flex items-center gap-3 text-xs">
                  <span className="flex-1 text-gray-300">{titleEn(l)} <span className="text-gray-500 font-mono">{l.article_number}</span></span>
                  <span className="text-gray-500">ordered {l.ordered_qty}</span>
                  <input type="number" min={0} max={l.ordered_qty} value={regQty[String(l.variation_id)] ?? 0}
                    onChange={e => setRegQty(q => ({ ...q, [String(l.variation_id)]: Number(e.target.value) }))}
                    className="keep-width w-16 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-right" />
                </div>
              ))}
              <button onClick={register} disabled={regBusy || regPreview.payment_kind !== 'cod' || !!regPreview.existing_case}
                className="px-4 py-1.5 rounded text-sm bg-green-700 hover:bg-green-600 text-white disabled:opacity-40">{regBusy ? 'Registering…' : 'Register the case'}</button>
            </div>
          )}
          {regMsg && <div className={`text-xs ${regMsg.startsWith('✓') ? 'text-green-400' : 'text-red-300'}`}>{regMsg}</div>}
        </div>
      )}
      <p className="text-xs text-gray-500 mb-3">
        {archived
          ? 'Orders already deleted in the shop, including everything imported from the spreadsheets.'
          : 'Parcels that came back never collected and never paid for. Delete the order in the shop — that is what puts the stock back — then mark it Done here.'}
      </p>

      {archived && (
        <input value={q} onChange={e => setQ(e.target.value)}
               placeholder="Search order number, customer or country…"
               className="mb-3 w-full max-w-sm bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm" />
      )}

      {err && <div className="mb-3 px-3 py-2 rounded bg-red-900/40 text-red-300 text-sm">{err}</div>}

      {loading ? <div className="text-gray-500 text-sm">Loading…</div>
       : cases.length === 0 ? (
        <div className="text-gray-500 text-sm py-10 text-center">
          {archived ? 'Nothing archived yet.' : 'Nothing waiting. '}
        </div>
      ) : (
        <div className="space-y-2">
          {cases.map(c => {
            const isOpen = open.has(c.id);
            const ready = c.progress.state === 'replenished';
            return (
              <div key={c.id} className="rounded-lg border border-gray-800 bg-gray-800/30">
                <div className="flex flex-wrap items-center gap-3 px-3 py-2.5 cursor-pointer"
                     onClick={() => toggle(c.id)}>
                  <span className="text-gray-500 text-xs w-12">{c.case_code}</span>
                  <span className="font-medium text-white w-24">{c.order_number}</span>
                  <span className="text-sm text-gray-400 flex-1 min-w-[10rem]">
                    {c.customer_name || '—'}
                    {c.country_code && <span className="text-gray-600 ml-2">{c.country_code}</span>}
                  </span>
                  <span className="text-sm text-gray-300 tabular-nums w-28 text-right">
                    {money(c.order_value, c.currency)}
                  </span>
                  <span className="text-sm text-gray-400 tabular-nums w-20 text-right">
                    {money(c.value_eur, 'EUR')}
                  </span>
                  {/* imported rows predate the WMS, so they have no polish/shelve
                      history — showing "Unpolished" against a 2024 parcel would be a
                      lie about work nobody was ever asked to do */}
                  {c.source === 'csv_import' ? (
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-500">
                      From the sheets
                    </span>
                  ) : c.registered_via ? (
                    <span className="text-xs px-2 py-0.5 rounded bg-sky-900/60 text-sky-200"
                          title={`Registered directly${c.received_by ? ` by ${c.received_by}` : ''} — no return order; ${c.carrier_state ? `carrier says ${c.carrier_state}` : 'no carrier tracking'}`}>
                      {c.registered_via === 'mcp' ? 'via Claude' : 'registered'}{c.status === 'discrepancy' ? ' · short' : ''}
                    </span>
                  ) : (
                    <span className={`text-xs px-2 py-0.5 rounded ${STATE_TONE[c.progress.state]}`}
                          title={`${c.progress.shelved}/${c.progress.expected} back on the shelf`}>
                      {STATE_LABEL[c.progress.state]}
                    </span>
                  )}
                  {archived ? (
                    <span className="text-xs text-gray-500 w-40 text-right">
                      {c.done_by ? `✓ ${c.done_by} · ` : 'deleted '}{day(c.deleted_in_shop_at)}
                    </span>
                  ) : (
                    <button onClick={e => { e.stopPropagation(); markDone(c); }}
                      title={ready || c.registered_via ? 'The order has been deleted in the shop'
                                   : 'Items are not all back on the shelf yet'}
                      className={`px-3 py-1 rounded text-sm font-medium w-40 ${
                        ready || c.registered_via ? 'bg-green-700 hover:bg-green-600 text-white'
                              : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}>
                      Done
                    </button>
                  )}
                </div>

                {isOpen && (
                  <div className="px-3 pb-3 pt-1 border-t border-gray-800/80">
                    <div className="text-xs text-gray-500 mb-2">
                      {c.source === 'csv_import' ? (
                        <>Ordered {day(c.order_date)} · imported from the spreadsheets, so there
                           is no item-level record of what came back</>
                      ) : c.registered_via ? (
                        <>Received {day(c.received_at)}{c.received_by ? ` · by ${c.received_by}` : ''} · registered directly, no return order
                           {c.notes ? ` · ${c.notes}` : ''}</>
                      ) : (
                        <>Received {day(c.received_at)} · {c.progress.shelved} of {c.progress.expected} pieces
                           back on the shelf
                           {c.progress.state !== 'replenished' &&
                            ' · finish the walk in the return order before marking it done'}</>
                      )}
                    </div>
                    {c.lines.length === 0 ? (
                      <div className="text-xs text-gray-600">No item detail recorded.</div>
                    ) : (
                    <table className="w-full text-sm">
                      <thead className="text-gray-600 text-xs">
                        <tr><th className="text-left py-1">Item</th>
                            <th className="text-left">Article</th>
                            <th className="text-right">Ordered</th>
                            <th className="text-right">Came back</th></tr>
                      </thead>
                      <tbody>
                        {c.lines.map(l => (
                          <tr key={l.id} className="border-t border-gray-800/60">
                            <td className="py-1 text-gray-300">{titleEn(l)}</td>
                            <td className="text-gray-500 font-mono text-xs">{l.article_number}</td>
                            <td className="text-right text-gray-400 tabular-nums">{l.qty_ordered}</td>
                            <td className={`text-right tabular-nums ${
                              l.qty_verified < l.qty_ordered ? 'text-red-400' : 'text-gray-400'}`}>
                              {l.qty_verified}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-gray-600 mt-5">
        Country trends, repeat customers and the CSV export are in
        <span className="text-gray-400"> Statistics → Unclaimed COD</span>.
      </p>
    </div>
  );
}
